import { PageHeader, LoadingState, DataTable } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, CardHeader, TextField, Button, Divider, Tab, Tabs, CircularProgress, Alert, Stack, InputAdornment, Accordion, AccordionSummary, AccordionDetails, Table, TableBody, TableCell, TableHead, TableRow, Paper, Container,
// Added
Avatar,
// Added
Chip,
// Added
useMediaQuery // Added
} from '@mui/material';
import { Chair as ChairIcon, MeetingRoom as RoomIcon, Person as PersonIcon, Save as SaveIcon, Calculate as CalculateIcon, ExpandMore as ExpandMoreIcon, Class as ClassIcon,
// Added
MenuBook as MenuBookIcon,
// Added
CheckCircle as CheckCircleIcon // Added
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles'; // Added
import api from '../utils/Api'; // Adjust path as needed

// ----------------------------------------------------------------------
// CONSTANTS & STRUCTURE
// ----------------------------------------------------------------------

const LEVELS = {
    KG: ['KG 1', 'KG 2'],
    Primary: ['Basic 1', 'Basic 2', 'Basic 3', 'Basic 4', 'Basic 5', 'Basic 6'],
    JHS: ['Basic 7', 'Basic 8', 'Basic 9']
};

// ----------------------------------------------------------------------
// HELPER COMPONENTS
// ----------------------------------------------------------------------


const ClassRecordForm = ({ className, level, initialData, liveEnrollment, onSave }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [formData, setFormData] = useState({
        infrastructure: {
            classrooms: 0,
            furniture: {
                monoDesks: 0,
                dualDesks: 0,
                teacherTables: 0,
                teacherChairs: 0,
                cupboards: 0,
                hexagonalTables: 0,
                roundTables: 0
            },
            boards: {
                markerBoards: 0,
                chalkBoards: 0
            }
        },
        enrolment: {
            male: 0,
            female: 0,
            total: 0
        }
    });

    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        // Base data from saved record
        const baseInfra = initialData?.infrastructure || {};
        const baseEnrol = initialData?.enrolment || {};

        // Live enrollment data (defaults to 0 if not provided)
        const liveBoys = liveEnrollment?.boys || 0;
        const liveGirls = liveEnrollment?.girls || 0;

        setFormData({
            infrastructure: {
                classrooms: baseInfra.classrooms || 0,
                furniture: {
                    monoDesks: baseInfra.furniture?.monoDesks || 0,
                    dualDesks: baseInfra.furniture?.dualDesks || 0,
                    hexagonalTables: baseInfra.furniture?.hexagonalTables || 0,
                    roundTables: baseInfra.furniture?.roundTables || 0,
                    teacherTables: baseInfra.furniture?.teacherTables || 0,
                    teacherChairs: baseInfra.furniture?.teacherChairs || 0,
                    cupboards: baseInfra.furniture?.cupboards || 0
                },
                boards: {
                    markerBoards: baseInfra.boards?.markerBoards || 0,
                    chalkBoards: baseInfra.boards?.chalkBoards || 0
                }
            },
            enrolment: {
                male: liveBoys,  // OVERRIDE with live data
                female: liveGirls, // OVERRIDE with live data
                total: liveBoys + liveGirls
            }
        });
    }, [initialData, liveEnrollment]);

    const handleChange = (section, subsection, field, value) => {
        const numValue = Number(value) || 0;

        setFormData(prev => {
            const newData = { ...prev };

            if (section === 'enrolment') {
                newData.enrolment = { ...newData.enrolment, [field]: numValue };
                newData.enrolment.total = (field === 'male' ? numValue : newData.enrolment.male) +
                    (field === 'female' ? numValue : newData.enrolment.female);
            } else if (section === 'infrastructure') {
                if (subsection === 'root') {
                    newData.infrastructure = { ...newData.infrastructure, [field]: numValue };
                } else {
                    newData.infrastructure[subsection] = { ...newData.infrastructure[subsection], [field]: numValue };
                }
            }
            return newData;
        });
        setHasChanges(true);
    };

    const handleSave = async (e) => {
        e.stopPropagation(); // Prevent accordion sort of toggle logic if button is inside summary (it isn't, but good practice if moved)
        setSaving(true);
        await onSave(className, level, formData);
        setSaving(false);
        setHasChanges(false);
    };

    // Calculate total furniture for this class for display in header
    const totalFurniture = Object.values(formData.infrastructure.furniture).reduce((sum, val) => sum + (Number(val) || 0), 0);

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{
                mb: 2,
                boxShadow: isMobile ? 1 : 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                overflow: 'hidden'
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${className}-content`}
                id={`${className}-header`}
                sx={{
                    bgcolor: expanded ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                    minHeight: 64
                }}
            >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', pr: 1 }}>
                    <Avatar sx={{
                        bgcolor: expanded ? theme.palette.primary.main : theme.palette.action.selected,
                        color: expanded ? 'white' : theme.palette.text.secondary,
                        width: 40, height: 40
                    }}>
                        {className.charAt(0)}
                    </Avatar>

                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" fontWeight="600" color={expanded ? "primary" : "text.primary"}>
                            {className}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                            <Chip
                                size="small"
                                icon={<PersonIcon fontSize="small" />}
                                label={`${formData.enrolment.total} Students`}
                                variant="outlined"
                            />
                            {!isMobile && (
                                <>
                                    <Chip
                                        size="small"
                                        icon={<RoomIcon fontSize="small" />}
                                        label={`${formData.infrastructure.classrooms} Rooms`}
                                        variant="outlined"
                                    />
                                    <Chip
                                        size="small"
                                        icon={<ChairIcon fontSize="small" />}
                                        label={`${totalFurniture} Items`}
                                        variant="outlined"
                                    />
                                </>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ p: isMobile ? 2 : 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                {/* Save Button at Top Right for easy access */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                    <Button
                        variant="contained"
                        size={isMobile ? "small" : "medium"}
                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                        sx={{ px: 4 }}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>

                <Grid container spacing={4}>
                    {/* Infrastructure Column */}
                    <Grid size={{ xs: 12, md: 6 }}   >
                        <Stack spacing={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'primary.main' }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
                                    <RoomIcon fontSize="small" />
                                </Avatar>
                                <Typography variant="h6">Infrastructure & Furniture</Typography>
                            </Box>

                            <Card variant="outlined" sx={{ p: 2, bgcolor: theme.palette.background.neutral }}>
                                <Stack spacing={2}>
                                    <TextField
                                        label="Number of Classrooms"
                                        fullWidth
                                        type="number"
                                        value={formData.infrastructure.classrooms}
                                        onChange={(e) => handleChange('infrastructure', 'root', 'classrooms', e.target.value)}
                                        InputProps={{ inputProps: { min: 0 } }}
                                    />

                                    <Divider sx={{ my: 1 }}>
                                        <Typography variant="caption" color="text.secondary">FURNITURE COUNT</Typography>
                                    </Divider>

                                    <Grid container spacing={2}>
                                        {level === 'KG' && (
                                            <>
                                                <Grid size={{ xs: 6 }}  >
                                                    <TextField label="Hexagonal Tables" size="small" fullWidth type="number"
                                                        value={formData.infrastructure.furniture.hexagonalTables}
                                                        onChange={(e) => handleChange('infrastructure', 'furniture', 'hexagonalTables', e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 6 }}  >
                                                    <TextField label="Round Tables" size="small" fullWidth type="number"
                                                        value={formData.infrastructure.furniture.roundTables}
                                                        onChange={(e) => handleChange('infrastructure', 'furniture', 'roundTables', e.target.value)}
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                        <Grid size={{ xs: 6 }}  >
                                            <TextField label="Mono Desks" size="small" fullWidth type="number"
                                                value={formData.infrastructure.furniture.monoDesks}
                                                onChange={(e) => handleChange('infrastructure', 'furniture', 'monoDesks', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 6 }}  >
                                            <TextField label="Dual Desks" size="small" fullWidth type="number"
                                                value={formData.infrastructure.furniture.dualDesks}
                                                onChange={(e) => handleChange('infrastructure', 'furniture', 'dualDesks', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 6 }}  >
                                            <TextField label="Teacher Tables" size="small" fullWidth type="number"
                                                value={formData.infrastructure.furniture.teacherTables}
                                                onChange={(e) => handleChange('infrastructure', 'furniture', 'teacherTables', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 6 }}  >
                                            <TextField label="Teacher Chairs" size="small" fullWidth type="number"
                                                value={formData.infrastructure.furniture.teacherChairs}
                                                onChange={(e) => handleChange('infrastructure', 'furniture', 'teacherChairs', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12 }}  >
                                            <TextField label="Cupboards" size="small" fullWidth type="number"
                                                value={formData.infrastructure.furniture.cupboards}
                                                onChange={(e) => handleChange('infrastructure', 'furniture', 'cupboards', e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ my: 1 }}>
                                        <Typography variant="caption" color="text.secondary">TEACHING BOARDS</Typography>
                                    </Divider>

                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 6 }}  >
                                            <TextField label="Marker Boards" size="small" fullWidth type="number"
                                                value={formData.infrastructure.boards.markerBoards}
                                                onChange={(e) => handleChange('infrastructure', 'boards', 'markerBoards', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 6 }}  >
                                            <TextField label="Chalk Boards" size="small" fullWidth type="number"
                                                value={formData.infrastructure.boards.chalkBoards}
                                                onChange={(e) => handleChange('infrastructure', 'boards', 'chalkBoards', e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Stack>
                            </Card>
                        </Stack>
                    </Grid>

                    {/* Enrolment Column */}
                    <Grid size={{ xs: 12, md: 6 }}   >
                        <Stack spacing={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'info.main' }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main' }}>
                                    <PersonIcon fontSize="small" />
                                </Avatar>
                                <Typography variant="h6">Student Enrollment</Typography>
                            </Box>

                            <Card variant="outlined" sx={{ p: 2 }}>
                                <Stack spacing={3}>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <TextField
                                            label="Male Students"
                                            fullWidth
                                            type="number"
                                            InputProps={{ startAdornment: <InputAdornment position="start">♂</InputAdornment> }}
                                            value={formData.enrolment.male}
                                            onChange={(e) => handleChange('enrolment', null, 'male', e.target.value)}
                                        />
                                        <TextField
                                            label="Female Students"
                                            fullWidth
                                            type="number"
                                            InputProps={{ startAdornment: <InputAdornment position="start">♀</InputAdornment> }}
                                            value={formData.enrolment.female}
                                            onChange={(e) => handleChange('enrolment', null, 'female', e.target.value)}
                                        />
                                    </Box>

                                    <Box sx={{
                                        p: 3,
                                        borderRadius: 2,
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary">TOTAL CLASS SIZE</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Based on live enrollment data
                                            </Typography>
                                        </Box>
                                        <Typography variant="h3" fontWeight="bold" color="primary.main">
                                            {formData.enrolment.total}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Card>
                        </Stack>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};


// ----------------------------------------------------------------------
// MAIN PAGE COMPONENT
// ----------------------------------------------------------------------

export default function SchoolRecordsPage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [currentTab, setCurrentTab] = useState('KG');
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [enrollmentData, setEnrollmentData] = useState([]);

    // Fetch records and enrollment
    const fetchData = async () => {
        try {
            setLoading(true);
            const [recordsRes, enrollmentRes] = await Promise.all([
                api.get('/school-records'),
                api.get('/enrollments/summary')
            ]);

            setRecords(recordsRes.data);

            // Handle enrollment response structure (aligned with EnrollmentPage.js logic)
            let classesRaw = [];
            if (enrollmentRes.data.success && enrollmentRes.data.classes) {
                classesRaw = enrollmentRes.data.classes;
            } else if (Array.isArray(enrollmentRes.data)) {
                classesRaw = enrollmentRes.data;
            } else if (enrollmentRes.data.classes) {
                classesRaw = enrollmentRes.data.classes;
            }
            setEnrollmentData(classesRaw);

            console.log('Records:', recordsRes.data);
            console.log('Enrollment:', classesRaw);
        } catch (err) {
            console.error('Failed to fetch data:', err);
            setError('Failed to load data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSaveRecord = async (className, level, data) => {
        try {
            // Optimistic update (optional, but let's just refetch or rely on card state)
            const payload = {
                className,
                level,
                infrastructure: data.infrastructure,
                enrolment: data.enrolment
            };

            const res = await api.put('/school-records', payload);

            // Update local state to reflect saved data (for totals calculation)
            setRecords(prev => {
                const existingIndex = prev.findIndex(r => r.className === className);
                if (existingIndex >= 0) {
                    const newRecords = [...prev];
                    newRecords[existingIndex] = res.data.record; // Update with server response
                    return newRecords;
                } else {
                    return [...prev, res.data.record];
                }
            });

            return true;
        } catch (err) {
            console.error('Save failed:', err);
            alert('Failed to save record.');
            return false;
        }
    };

    // Helper to find live enrollment for a given class name (e.g. "KG 1")
    // Sums up if there are multiple sections like "KG 1 A", "KG 1 B"
    const getLiveEnrollment = (targetName) => {
        // Exact match or Starts With match?
        // "KG 1" matches "KG 1", "KG 1 A", "KG 1 B"
        // "Basic 1" matches "Basic 1", "Basic 1 A"

        // Normalize target name for comparison
        const targetNorm = targetName.toLowerCase().replace(/\s+/g, '');

        const relevantClasses = enrollmentData.filter(cls => {
            const clsName = (cls.classDisplayName || cls.className || cls.name || '').toLowerCase().replace(/\s+/g, '');
            // Check for exact match OR starts with match (to capture sections)
            return clsName === targetNorm || clsName.startsWith(targetNorm);
        });

        return relevantClasses.reduce((acc, cls) => {
            acc.boys += (cls.boys || 0);
            acc.girls += (cls.girls || 0);
            return acc;
        }, { boys: 0, girls: 0 });
    };

    // Calculate totals for a specific level
    const getLevelTotals = (level) => {
        const levelClasses = LEVELS[level];

        return levelClasses.reduce((acc, className) => {
            // Find saved record
            const r = records.find(rec => rec.className === className) || {};
            // Find live enrollment (which overrides saved record for enrollment counts)
            const live = getLiveEnrollment(className);

            // Use live if available, else saved, else 0? 
            // User request: "take number from EnrollmentPage". implies Live is truth.
            const maleCount = live.boys;
            const femaleCount = live.girls;
            const totalCount = maleCount + femaleCount;

            acc.classrooms += (r.infrastructure?.classrooms || 0);
            acc.furniture += (
                (r.infrastructure?.furniture?.monoDesks || 0) +
                (r.infrastructure?.furniture?.dualDesks || 0) +
                (r.infrastructure?.furniture?.hexagonalTables || 0) +
                (r.infrastructure?.furniture?.roundTables || 0) +
                (r.infrastructure?.furniture?.teacherTables || 0) +
                (r.infrastructure?.furniture?.teacherChairs || 0) +
                (r.infrastructure?.furniture?.cupboards || 0)
            );
            acc.male += maleCount;
            acc.female += femaleCount;
            acc.total += totalCount;
            return acc;
        }, { classrooms: 0, furniture: 0, male: 0, female: 0, total: 0 });
    };

    const totals = getLevelTotals(currentTab);

    if (loading && records.length === 0) {
        return (
            <LoadingState />
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
            {/* Header */}
            <PageHeader title={<>School Records Management</>} description={<>Manage infrastructure, furniture, and student enrolment for KG, Primary, and JHS levels.</>}></PageHeader>
      <SummaryStrip container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}    >
                    <SummaryMetric sx={{
                        boxShadow: isMobile ? 1 : 3,
                        border: `1px solid ${theme.palette.primary.main}`,
                        transition: 'all 0.3s ease',
                        '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: isMobile ? 2 : 6 }
                    }}>
                        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: isMobile ? 40 : 48, height: isMobile ? 40 : 48 }}>
                                    <PersonIcon fontSize={isMobile ? "small" : "medium"} />
                                </Avatar>
                                <Box>
                                    <Typography variant={isMobile ? "body2" : "h6"} color="text.secondary">Total Students</Typography>
                                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">{totals.total}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {totals.male} Boys • {totals.female} Girls
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </SummaryMetric>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}    >
                    <SummaryMetric sx={{
                        boxShadow: isMobile ? 1 : 3,
                        border: `1px solid ${theme.palette.info.main}`,
                        transition: 'all 0.3s ease',
                        '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: isMobile ? 2 : 6 }
                    }}>
                        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ bgcolor: theme.palette.info.main, width: isMobile ? 40 : 48, height: isMobile ? 40 : 48 }}>
                                    <RoomIcon fontSize={isMobile ? "small" : "medium"} />
                                </Avatar>
                                <Box>
                                    <Typography variant={isMobile ? "body2" : "h6"} color="text.secondary">Classrooms</Typography>
                                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">{totals.classrooms}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Active classrooms
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </SummaryMetric>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}    >
                    <SummaryMetric sx={{
                        boxShadow: isMobile ? 1 : 3,
                        border: `1px solid ${theme.palette.warning.main}`,
                        transition: 'all 0.3s ease',
                        '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: isMobile ? 2 : 6 }
                    }}>
                        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ bgcolor: theme.palette.warning.main, width: isMobile ? 40 : 48, height: isMobile ? 40 : 48 }}>
                                    <ChairIcon fontSize={isMobile ? "small" : "medium"} />
                                </Avatar>
                                <Box>
                                    <Typography variant={isMobile ? "body2" : "h6"} color="text.secondary">Total Furniture</Typography>
                                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">{totals.furniture}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Desks, tables, chairs
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </SummaryMetric>
                </Grid>
            </SummaryStrip>

            {/* Tabs */}
            <Card sx={{
                mb: isMobile ? 3 : 4,
                boxShadow: isMobile ? 1 : 3,
                border: `1px solid ${theme.palette.divider}`
            }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                    <Tabs
                        value={currentTab}
                        onChange={(e, val) => setCurrentTab(val)}
                        variant={isMobile ? "scrollable" : "fullWidth"}
                        scrollButtons="auto"
                        textColor="primary"
                        indicatorColor="primary"
                    >
                        <Tab icon={<PersonIcon />} iconPosition="start" label="Kindergarten (KG)" value="KG" />
                        <Tab icon={<MenuBookIcon />} iconPosition="start" label="Primary School" value="Primary" />
                        <Tab icon={<ClassIcon />} iconPosition="start" label="Junior High School (JHS)" value="JHS" />
                    </Tabs>
                </CardContent>
            </Card>

            {/* Stats Cards */}
            

            {/* Level Summary Table */}
            <Card sx={{
                mb: isMobile ? 3 : 4,
                boxShadow: isMobile ? 1 : 3,
                border: `1px solid ${theme.palette.divider}`
            }}>
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{
                        p: isMobile ? 2 : 3,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}>
                        <Avatar sx={{
                            bgcolor: theme.palette.info.main,
                            width: 40,
                            height: 40
                        }}>
                            <CalculateIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="h6" fontWeight="600">
                                {currentTab} Enrollment Summary
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Overview of student counts by gender
                            </Typography>
                        </Box>
                    </Box>

                    <DataTable component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
                        <Table size={isMobile ? "small" : "medium"}>
                            <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>CLASS</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>MALE</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>FEMALE</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>TOTAL</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {LEVELS[currentTab].map((className) => {
                                    const live = getLiveEnrollment(className);
                                    const male = live.boys;
                                    const female = live.girls;
                                    const total = male + female;

                                    return (
                                        <TableRow
                                            key={className}
                                            sx={{
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: theme.palette.grey[300] }}>
                                                        {className.charAt(0)}
                                                    </Avatar>
                                                    <Typography variant="subtitle2">{className}</Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label={male} size="small" variant="outlined" color="info" />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label={female} size="small" variant="outlined" color="secondary" />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography fontWeight="bold" color="primary.main">{total}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {/* Grand Total Row */}
                                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>GRAND TOTAL</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{totals.male}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{totals.female}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'primary.dark', fontSize: '1.1rem' }}>{totals.total}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </DataTable>
                </CardContent>
            </Card>

            {/* Class Records */}
            <Box>
                {LEVELS[currentTab].map((className) => {
                    const record = records.find(r => r.className === className);
                    const liveEnrollment = getLiveEnrollment(className);
                    return (
                        <ClassRecordForm
                            key={className}
                            className={className}
                            level={currentTab}
                            initialData={record}
                            liveEnrollment={liveEnrollment}
                            onSave={handleSaveRecord}
                        />
                    );
                })}
            </Box>
        </Container>
    );
}
